
import { ISbStoryData } from '@storyblok/js';
import gsap from '~/plugins/gsap';
import StoryblokClient from 'storyblok-js-client';
import Vue from 'vue';
import { transformLink, TransformLinkReturn, transformRichText } from '~/transforms/transformComponents';
import { NavConfigStoryblok } from '~/types/storyblok-generated';

export default Vue.extend({
  props: {
    category: {
      type: String,
    },
    state: {
      type: Boolean,
    },
    currentHeadline: {
      type: String,
    },
    currentPageUid: {
      type: String,
    },
  },
  data() {
    return {
      Storyblok: new StoryblokClient({
        accessToken: process.env.STORYBLOK_API_KEY,
      }),
      navInline: false as Boolean,
      categoryLabel: '' as any,
      navLinksFromStories: Array() as (NonNullable<TransformLinkReturn> & { uuid: string })[],
      navLinksCustom: Array() as (NonNullable<TransformLinkReturn> & { uuid: string })[],
      serviceData: null as any,
      service: false as boolean,
      showLayer: false as boolean,
      moveToLogo: false as boolean,
    };
  },
  methods: {
    openLayer() {
      this.showLayer = true;
      const currentQuerry = { ...this.$route.query };
      if (currentQuerry.formSent) delete currentQuerry.formSent;
      this.$router.push({ query: { ...currentQuerry, ...{ showForm: '1' } } });
      //
      gsap.config({ nullTargetWarn: false });
      //
      setTimeout(() => {
        gsap.set(['.c-nav-inline__layer'], { yPercent: 100, opacity: 1 });
        gsap.to(['.c-nav-inline__layer'], { yPercent: 0, ease: 'curveAppear', duration: 0.6, delay: 0.1 });
        gsap.to(['.c-nav-inline__layer-bg'], { opacity: 0.5, ease: 'curveAppear', duration: 0.3 });
      }, 100);

      window.addEventListener('modalClose', this.closeLayer, { passive: true });
    },
    closeLayer() {
      if (this.$route.query.showForm) {
        const currentQuery = { ...this.$route.query };
        if (currentQuery.formSent) delete currentQuery.formSent;
        if (currentQuery.success) delete currentQuery.success;
        if (currentQuery.showForm) delete currentQuery.showForm;
        this.$router.push({ query: currentQuery });
      }

      gsap.config({ nullTargetWarn: false });
      //
      gsap.to(['.c-nav-inline__layer'], { yPercent: 100, ease: 'curveAppear', duration: 0.4 });
      gsap.to(['.c-nav-inline__layer-bg'], { opacity: 0, ease: 'curveAppear', duration: 0.3, delay: 0.1 });
      //
      setTimeout(() => {
        this.showLayer = false;
      }, 400);

      window.removeEventListener('modalClose', this.closeLayer);
    },
    replaceCharacter(str: string) {
      return str.replace('ae', 'ä').replace('oe', 'ö').replace('ue', 'ü');
    },

    async navInlineFunction() {
      // get link if nav inline property is true
      const { market, lang } = this.$i18n.localeProperties;

      const [pageQueryResult, configQueryResult] = await Promise.all([
        this.Storyblok.get(`cdn/stories`, {
          version: process.env.STORYBLOK_CONTENT_VERSION,
          resolve_links: 'url',
          by_slugs: `${market}-${lang}/*${this.category}/*`,
          filter_query: {
            navInline: {
              is: true,
            },
            navInlineHideSelf: {
              is: false,
            },
          },
        }),
        this.Storyblok.get(`cdn/stories`, {
          version: process.env.STORYBLOK_CONTENT_VERSION,
          resolve_links: 'url',
          by_slugs: `${market}-${lang}/*${this.category}/*`,
          filter_query: {
            component: {
              in: 'nav-config',
            },
          },
        }),
      ]);
      const pageStories = pageQueryResult.data.stories as ISbStoryData[];
      const config: NavConfigStoryblok | undefined = configQueryResult.data.stories?.[0]?.content;

      // Build nav data based on category stories with navInline property and custom links from category nav config
      this.navLinksFromStories = pageStories
        .map(dataItem => {
          const link = transformLink({
            url: dataItem.full_slug,
            label: dataItem.name,
            showRed: dataItem.content.navInlineRed,
          });
          return link ? { ...link, uuid: dataItem.uuid } : undefined;
        })
        .filter(link => link !== undefined);
      this.navLinksCustom = (config?.customNavigationLinks ?? [])
        .map(button => {
          const link = transformLink({ ...button.link, label: button.label });
          return link ? { ...link, uuid: button.link?.story?.uuid ?? '' } : undefined;
        })
        .filter(link => link !== undefined);

      // Category label
      if (!!config?.categoryLabel) {
        this.categoryLabel = config.categoryLabel;
      }

      // Move to logo
      if (config?.moveToLogo === true) {
        this.moveToLogo = true;
        this.$emit('moveToLogo');
      }

      // Service button
      if (config?.serviceButton === true && config.formLayer?.[0]) {
        this.serviceData = {
          serviceButton: config.serviceButton,
          button: config.buttonLabel,
          formLayer: {
            moduleHeader: {
              headline: {
                text: config.formLayer[0].headlineText,
                level: 'h2',
                fontStyle: 'h3',
              },
              copy: config.formLayer[0].copy,
            },
            id: config.formLayer[0].id,
            contactAddress: config.formLayer[0].contactAddress,
            buttonLabel: config.formLayer[0].buttonLabel,
            marginal: transformRichText(config.formLayer[0].marginal),
            contactForm: transformRichText(config.formLayer[0].contactForm),
            confirmation: transformRichText(config.formLayer[0].confirmation),
          },
        };
        this.service = true;
      }
    },
  },
  mounted() {
    if (this.category) {
      this.navInlineFunction();
    }
    if (this.$route.query.showForm && !this.$route.query.formSent) this.openLayer();
    if (this.$route.query.showForm && this.$route.query.formSent) {
      this.closeLayer();
    }
    if (this.state === true) {
      this.navInline = true;
    }
  },
});
